// Extracted types from schema.ts to avoid dependency on generated GraphQL types
import { type ThemedAsset } from '../themed-image/ThemedImage';

import type { Money } from '@betterment/core';

export const LegalAccountTaxationType = {
  Holding_529: 'HOLDING_529',
  Hsa: 'HSA',
  Roth_401K: 'ROTH_401K',
  RothIra: 'ROTH_IRA',
  SepIra: 'SEP_IRA',
  Taxable: 'TAXABLE',
  Traditional_401K: 'TRADITIONAL_401K',
  TraditionalIra: 'TRADITIONAL_IRA',
} as const;

export type LegalAccountTaxationType = (typeof LegalAccountTaxationType)[keyof typeof LegalAccountTaxationType];

type Security = {
  id: string;
  symbol: string;
  name?: string;
  url?: string;
  __typename: 'SecurityFund' | 'SecurityStock';
  expenseRatio?: {
    value: number;
  };
};

export type RetailSecurityPosition = {
  security: Security;
  amount: Money;
  shares: number;
};

export type InstitutionalSecurityPosition = {
  security: Security;
  financialSecurity: FinancialSecurity;
  amount: Money;
  shares: number;
};

export type SecurityPosition = RetailSecurityPosition | InstitutionalSecurityPosition;

export function isInstitutionalSecurityPosition(position: SecurityPosition): position is InstitutionalSecurityPosition {
  return 'financialSecurity' in position;
}

type RetailSecurityPositionForSubAccount = {
  security: Security;
};

type InstitutionalSecurityPositionForSubAccount = {
  security: Security;
  financialSecurity: FinancialSecurity;
};

type SecurityPositionForSubAccount = RetailSecurityPositionForSubAccount | InstitutionalSecurityPositionForSubAccount;

export function isInstitutionalSecurityPositionForSubAccount(
  position: SecurityPositionForSubAccount
): position is InstitutionalSecurityPositionForSubAccount {
  return 'financialSecurity' in position;
}

export type SecurityPositionBySecurity = {
  id: string;
  currentWeight: number;
  securityPositionsBySubAccount: Array<{
    amount: Money;
    shares: number;
    subAccount: {
      id: string;
      name: string;
      legalAccount?: {
        taxationType: LegalAccountTaxationType;
      };
    };
    securityPositions: Array<SecurityPositionForSubAccount>;
  }>;
};

export type SecurityGroupPosition = {
  id: string;
  amount: Money; // Money type from schema
  currentWeight: number; // Percent type from schema
  averageExpenseRatioPercent: number;
  drift: number;
  fundFees: Money;
  isRogueSecurity?: boolean;
  securityGroup: {
    name: string;
    description?: string | null;
    targetWeight: number;
  };
  securityPositions: Array<SecurityPosition>;
  securityPositionsBySecurity: Array<SecurityPositionBySecurity>;
};

type CompanyProfile = {
  logo?: ThemedAsset | null;
};

type FinancialSecurityBase = {
  id: string;
  symbol: string;
  name: string;
};

type FinancialSecurityFund = FinancialSecurityBase & {
  __typename: 'FinancialSecurityFund';
};

type FinancialSecurityStock = FinancialSecurityBase & {
  companyProfile?: CompanyProfile | null;
  __typename: 'FinancialSecurityStock';
};

type FinancialSecurity = FinancialSecurityFund | FinancialSecurityStock;

export type CashBalance = {
  availableBalance: Money;
  totalBalance: Money;
  anyPending: boolean;
};

type PositionReturn = {
  amount: Money;
  percent: number;
};

export type DirectSecurityPosition = {
  amount: Money;
  hasPendingOrders: boolean;
  shares: number;
  financialSecurity: FinancialSecurity;
  totalReturn?: PositionReturn | null;
  todaysReturn?: PositionReturn | null;
};

export type DirectTradingAccount = {
  id: string;
  securityPositions: Array<DirectSecurityPosition>;
};
